import type { MarketData } from '@/common/MarketData.ts';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { OrderType } from '@/feature/order_entry/order_types.ts';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import type { ViewPosition } from '@/feature/terminal_tables/position/types.ts';
import { bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';
import useAppStore from '../../../store/rootStore.ts';
import { clampReduceOnlySize, resolveOrderSize, resolveSizingPrice } from './orderSizing.ts';

/** Order packers require the exchange config to be loaded before building requests. */
export function assertExchangeTokenAvailable(): void {
  if (!useAppStore.getState().exchange?.token) {
    throw new Error('Exchange token not available');
  }
}

export type OrderPriceValues = {
  priceBigInt: bigint;
  sizeBigInt: bigint;
  stopPriceBigInt: bigint;
  position: ViewPosition | undefined;
};

function convertOrderValueToBigInt(value: string | undefined, decimals: number): bigint {
  if (value == null || value.trim() === '') {
    return 0n;
  }
  const bigValue = stringToBig(value);
  return bigToBigInt(bigValue, decimals);
}

function findPosition(marketId: number | undefined): ViewPosition | undefined {
  if (marketId == null) return undefined;
  return usePositionStore.getState().positions.find((p) => p.marketId === marketId);
}

export function extractPriceValues(formData: OrderFormData, marketData: MarketData): OrderPriceValues {
  const stopPriceValue = 'stopPrice' in formData && formData.stopPrice?.trim() ? formData.stopPrice : undefined;
  const limitPriceValue = 'price' in formData && formData.price?.trim() ? formData.price : undefined;

  const priceBigInt = convertOrderValueToBigInt(limitPriceValue, marketData.quoteDecimals);
  const stopPriceBigInt = convertOrderValueToBigInt(stopPriceValue, marketData.quoteDecimals);

  const sizingPrice = resolveSizingPrice(formData, marketData);
  const { assetSize, atomicSize } = resolveOrderSize(formData, sizingPrice, marketData.assetDecimals);
  if (assetSize.gt(0) && atomicSize === 0n) {
    throw new Error('Order size is too small for this market.');
  }

  // Clamped here so every consumer — main order, on-chain order, TP/SL legs — ships the same size.
  const position = findPosition(marketData.id);
  const sizeBigInt = clampReduceOnlySize(formData, position, atomicSize, marketData.assetDecimals);

  return { priceBigInt, sizeBigInt, stopPriceBigInt, position };
}

export function determineFinalPrice(formData: OrderFormData, priceValues: OrderPriceValues): bigint {
  if (formData.orderType === OrderType.stopLimit) {
    return priceValues.priceBigInt;
  }

  if (formData.orderType === OrderType.stopMarket) {
    return priceValues.stopPriceBigInt;
  }

  return priceValues.priceBigInt;
}
